import { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  Popconfirm,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useMappingsStore } from '../store/mappings';
import { useNotificationsStore } from '../store/notifications';
import type { MappingTemplate } from '../services/api/mapping';
import { format } from 'date-fns';

const { Title } = Typography;

export function MappingList() {
  const navigate = useNavigate();
  const {
    mappings,
    loading,
    error,
    total,
    currentOffset,
    pageSize,
    fetchMappings,
    deleteMapping,
    clearError,
  } = useMappingsStore();
  const { success, error: showError } = useNotificationsStore();

  const [searchText, setSearchText] = useState('');
  const [searchKey, setSearchKey] = useState('');
  const [searchLabels, setSearchLabels] = useState('');
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  const [filterStatus, setFilterStatus] = useState<string | undefined>(
    undefined
  );
  const [filterCreator, setFilterCreator] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(25);

  // Debounced fetch
  const debouncedFetch = useCallback(() => {
    const timeoutId = setTimeout(() => {
      const offset = (currentPage - 1) * currentPageSize;
      fetchMappings({
        limit: currentPageSize,
        offset,
        name: searchText || undefined,
        key: searchKey || undefined,
        labels: searchLabels || undefined,
        docType: filterType,
        status: filterStatus,
        creator: filterCreator || undefined,
        owner: filterOwner || undefined,
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [
    searchText,
    searchKey,
    searchLabels,
    filterType,
    filterStatus,
    filterCreator,
    filterOwner,
    currentPage,
    currentPageSize,
    fetchMappings,
  ]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchText,
    searchKey,
    searchLabels,
    filterType,
    filterStatus,
    filterCreator,
    filterOwner,
  ]);

  useEffect(() => {
    const cleanup = debouncedFetch();
    return cleanup;
  }, [debouncedFetch]);

  useEffect(() => {
    if (error) {
      showError('Error', error);
      clearError();
    }
  }, [error, showError, clearError]);

  const handleDelete = async (key: string, name: string) => {
    try {
      await deleteMapping(key);
      success('Deleted', `Mapping "${name}" has been deleted`);
    } catch (err) {
      showError('Delete Failed', 'Failed to delete mapping');
    }
  };

  const handleCreate = () => {
    navigate('/mappings/create');
  };

  const handleView = (key: string) => {
    navigate(`/mappings/${key}`);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: MappingTemplate, b: MappingTemplate) =>
        a.name.localeCompare(b.name),
      render: (text: string, record: MappingTemplate) => (
        <Button
          type="link"
          onClick={() => handleView(record.key)}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Document Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => {
        const colors: Record<string, string> = {
          po: 'blue',
          inv: 'purple',
          receipt: 'orange',
          contract: 'cyan',
        };
        const labels: Record<string, string> = {
          po: 'PO',
          inv: 'Invoice',
          receipt: 'Receipt',
          contract: 'Contract',
        };
        return (
          <Tag color={colors[type] || 'default'}>
            {labels[type] || type.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Labels',
      dataIndex: 'labels',
      key: 'labels',
      width: 200,
      render: (labels: string[]) => (
        <>
          {labels && labels.length > 0 ? (
            labels.slice(0, 2).map((label, index) => (
              <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                {label}
              </Tag>
            ))
          ) : (
            <span style={{ color: '#999' }}>-</span>
          )}
          {labels && labels.length > 2 && (
            <Tag color="default">+{labels.length - 2}</Tag>
          )}
        </>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: string) => {
        const colors: Record<string, string> = {
          NEW: 'default',
          AUTO_MAPPING: 'processing',
          IN_PROGRESS: 'warning',
          COMPLETED: 'success',
          IN_VERIFICATION: 'purple',
          VERIFIED: 'green',
        };
        const labels: Record<string, string> = {
          NEW: 'New',
          AUTO_MAPPING: 'Auto Mapping',
          IN_PROGRESS: 'In Progress',
          COMPLETED: 'Completed',
          IN_VERIFICATION: 'In Verification',
          VERIFIED: 'Verified',
        };
        return (
          <Tag color={colors[status] || 'default'}>
            {labels[status] || status}
          </Tag>
        );
      },
    },
    {
      title: 'Creator',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 150,
      render: (creator: string) => creator || '-',
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      sorter: (a: MappingTemplate, b: MappingTemplate) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (date: string) => format(new Date(date), 'MMM dd, yyyy'),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      sorter: (a: MappingTemplate, b: MappingTemplate) =>
        new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      render: (date: string) => format(new Date(date), 'MMM dd, yyyy'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: MappingTemplate) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(record.key)}
            title="View"
          />
          {['AUTO_MAPPING', 'IN_PROGRESS', 'COMPLETED', 'NEW'].includes(
            record.status
          ) && (
            <Button
              type="text"
              icon={<CheckCircleOutlined />}
              onClick={() =>
                navigate(`/mappings/${record.key}`, {
                  state: { activeTab: 'verification' },
                })
              }
              title="Verify"
              style={{ color: '#1890ff' }}
            />
          )}
          {/* Template editing is now done in the verification view (Advanced tab → Edit field boxes) */}
          {/* Delete — not yet implemented
          <Popconfirm
            title="Delete mapping"
            description={`Are you sure you want to delete "${record.name}"?`}
            onConfirm={() => handleDelete(record.key, record.name)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete"
            />
          </Popconfirm>
          */}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Mappings
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="large"
        >
          Create Mapping
        </Button>
      </div>

      <div
        style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}
      >
        <Input
          placeholder="Search by name..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 220 }}
          allowClear
        />
        <Input
          placeholder="Search by key..."
          prefix={<SearchOutlined />}
          value={searchKey}
          onChange={(e) => setSearchKey(e.target.value)}
          style={{ width: 200 }}
          allowClear
        />
        <Input
          placeholder="Search by labels..."
          prefix={<SearchOutlined />}
          value={searchLabels}
          onChange={(e) => setSearchLabels(e.target.value)}
          style={{ width: 200 }}
          allowClear
        />
        <Select
          placeholder="Document type"
          style={{ width: 160 }}
          value={filterType}
          onChange={setFilterType}
          allowClear
          options={[
            { label: 'PO', value: 'po' },
            { label: 'Invoice', value: 'inv' },
            { label: 'Receipt', value: 'receipt' },
            { label: 'Contract', value: 'contract' },
          ]}
        />
        <Select
          placeholder="Status"
          style={{ width: 160 }}
          value={filterStatus}
          onChange={setFilterStatus}
          allowClear
          options={[
            { label: 'New', value: 'NEW' },
            { label: 'Auto Mapping', value: 'AUTO_MAPPING' },
            { label: 'In Progress', value: 'IN_PROGRESS' },
            { label: 'Completed', value: 'Completed' },
            { label: 'In Verification', value: 'In Verification' },
            { label: 'Verified', value: 'VERIFIED' },
            { label: 'Waiting For Scripting', value: 'Waiting For Scripting' },
          ]}
        />
        <Input
          placeholder="Creator..."
          prefix={<SearchOutlined />}
          value={filterCreator}
          onChange={(e) => setFilterCreator(e.target.value)}
          style={{ width: 160 }}
          allowClear
        />
        <Input
          placeholder="Owner..."
          prefix={<SearchOutlined />}
          value={filterOwner}
          onChange={(e) => setFilterOwner(e.target.value)}
          style={{ width: 160 }}
          allowClear
        />
      </div>

      <Table
        columns={columns}
        dataSource={mappings}
        rowKey="key"
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: currentPageSize,
          total: total,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} mappings`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setCurrentPageSize(size);
          },
          pageSizeOptions: ['10', '25'],
        }}
        scroll={{ x: 1600 }}
      />
    </div>
  );
}
